import { ActionContext, Module } from 'vuex';
import { TextState, RootState } from '@/utils/types/store';
import { TextDTO, TextReadabilityDTO } from '@/utils/types/text';
import { Paragraph } from '@/utils/types/common';
import names from '@/store/text/names';
import helpers from '@/store/helpers';
import api from '@/config/api';
import Vue from 'vue';

type Ctx = ActionContext<TextState, RootState>;

const initialState: TextState = {
  // All texts
  texts: [],
  // Currently viewed text
  text: {} as TextDTO,
  // Currently selected paragraph
  paragraph: {} as Paragraph,
  // Readability of currently viewed text
  textReadability: {} as TextReadabilityDTO,
};

const textModule: Module<TextState, RootState> = {
  namespaced: true,
  state: initialState,
  getters: {
    textById: (state: TextState) => (id: string): TextDTO | null => {
      const text = state.texts.find((t: TextDTO) => t.id === id);
      return text || null;
    },
  },
  mutations: {
    [names.mutations._SAVE_TEXTS](state, texts) {
      state.texts = texts;
    },
    [names.mutations._SAVE_TEXT](state, text) {
      state.text = text;
    },
    [names.mutations._SAVE_PARAGRAPH](state, paragraph) {
      state.paragraph = paragraph;
    },
    [names.mutations._SAVE_TEXT_READABILITY](state, textReadability) {
      state.textReadability = textReadability;
    },
  },
  actions: {
    loadTexts({ commit }: Ctx) {
      return helpers.axios.get(commit)(
        api.routes.texts,
        names.mutations._SAVE_TEXTS,
      );
    },
    loadText({ commit, getters }: Ctx, id: string) {
      const text = getters.textById(id) as TextDTO;
      return commit('SAVE_TEXT', text);
    },
    loadTextReadability({ commit }: Ctx, docId: string) {
      return helpers.axios.get(commit)(
        `${api.routes.texts}/${docId}/readability`,
        names.mutations._SAVE_TEXT_READABILITY,
      );
    },
    loadParagraph({ commit }: Ctx, {
      docId,
      paragraphNumber,
    }: { docId: string; paragraphNumber: number }) {
      return helpers.axios.get(commit)(
        `${api.routes.texts}/${docId}/paragraph/${paragraphNumber}`,
        names.mutations._SAVE_PARAGRAPH,
      );
    },
    postText({ commit, state }: Ctx, payload: FormData) {
      return Vue.axios
        .post(api.routes.texts, payload)
        .then((result) => {
          const newTexts = [...state.texts, result.data];
          commit(names.mutations._SAVE_TEXTS, newTexts);
        });
    },
    deleteText({ dispatch }: Ctx, docId: string) {
      return Vue.axios
        .delete(`${api.routes.texts}/${docId}`)
        .then(() => dispatch('loadTexts'))
        .catch((error) => {
          throw new Error(`API ${error}`);
        });
    },
    clearText({ commit }: Ctx) {
      commit(names.mutations._SAVE_TEXT, {});
      commit(names.mutations._SAVE_TEXT_READABILITY, {});
      commit(names.mutations._SAVE_PARAGRAPH, {});
    },
  },
};

export default textModule;
